import React, { useState, useEffect, useMemo, useRef } from "react";
import '../CalmLeafChatbot/style.css';
import { BackIcon, SendIcon, EllipsisIcon, CancelIcon } from "../../assets/icons/icons";
import { supabase } from '../../supabaseClient';
import exercisesData from '../../assets/exercises.json';
import ExerciseModal from '../ExerciseModal/ExerciseModal';
import { apiFetch } from '../../api/apiClient';
import { useBrand } from '../../branding/BrandProvider';

const DEFAULT_APP_NAME = 'ColeDitioned Coaching';
const buildDisclaimers = (appName) => ({
  inline: `${appName} is AI-enabled tech and may contain errors. Responses are not medical advice, any promoted products have not been evaluated by the FDA, and nothing shared is intended to diagnose, treat, cure, or prevent any disease.`,
  full: `${appName} does not provide medical advice. All responses are AI-generated and offer general wellness information derived from public sources. Guidance is not personalized, may highlight ${appName} partner offerings, and can contain errors. Always consult a qualified healthcare professional before making health decisions or starting new fitness routines.`,
});

const dashCharacters = '\u2010\u2011\u2012\u2013\u2014\u2015\u2212-';
const dashCheckPattern = new RegExp(`[${dashCharacters}]`);
const dashReplacePattern = new RegExp(`[${dashCharacters}]`, 'g');
const bulletPattern = new RegExp(`^[${dashCharacters}]+\\s+`);
const rangePattern = new RegExp(`(\\d+)\\s*[${dashCharacters}]\\s*(\\d+)`, 'g');
const PLAN_STATUS_CACHE_MS = 2 * 60 * 1000;
const PLAN_DOMAIN_WORKOUT = ['workout', 'training', 'lift', 'lifting', 'gym', 'exercise'];
const PLAN_DOMAIN_MEAL = ['meal', 'meals', 'diet', 'nutrition', 'macro', 'macros', 'calorie', 'calories', 'food', 'eating'];
const PLAN_CONTEXT_WORDS = ['plan', 'program', 'routine', 'schedule', 'split', 'menu'];
const PLAN_TIME_WORDS = ['today', 'tomorrow', 'this week', 'weekly', 'week', 'day', 'days'];

const TypingIndicator = () => (
  <span className="typing-indicator" aria-label="Assistant is typing">
    <span />
    <span />
    <span />
  </span>
);

const CoachChatbot = ({ userId }) => {
  const [userQuestion, setUserQuestion] = useState("");
  const [chatLog, setChatLog] = useState([]);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [uid, setUid] = useState(userId || null);
  const [authToken, setAuthToken] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [reportText, setReportText] = useState("");
  const [reportStatus, setReportStatus] = useState("idle");
  const [isMobile, setIsMobile] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const { name: brandName } = useBrand();
  const appName = brandName || DEFAULT_APP_NAME;
  const { inline: disclaimerInline, full: disclaimerFull } = useMemo(
    () => buildDisclaimers(appName),
    [appName]
  );

  const [openExercise, setOpenExercise] = useState(null);
  const taRef = useRef(null);
  const messagesRef = useRef(null);
  const planStatusRef = useRef({ workout: null, meal: null, lastChecked: 0 });

  const imgCtx = useMemo(() => {
    try { return require.context('../../assets/exercises', true); }
    catch { return null; }
  }, []);
  const getImage = (relativePath) => {
    if (!imgCtx) return '';
    try { return imgCtx('./' + relativePath); } catch { return ''; }
  };

  const allSuggestedPrompts = [
    "How fast will I see results?",
    "What's a good leg warm-up?",
    "How much protein do I need?",
    "Tips to sleep better for recovery",
    "Meal ideas after lifting",
    "What should I eat before a morning workout?",
    "Give me a quick mobility routine",
    "How do I fix my bench press form?",
    "What’s a simple grocery list for clean eating?",
    "Ideas for hitting 10k steps when I work from home",
    "How do I recover faster between heavy days?",
    "Can you remind me how to track my macros?",
    "What’s a solid push day finisher?",
    "How do I know if my protein shake is helping?",
    "Best way to warm up knees before squats?",
    "What’s a healthy snack for late nights?",
    "How do I adjust calories when I'm sick?",
    "Give me a mental reset routine between meetings",
    "What core work should I add after leg day?",
    "How do I improve sleep on travel days?"
  ];

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 798);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    setTimeout(() => setAnimateIn(true), 50);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const shuffled = [...allSuggestedPrompts].sort(() => 0.5 - Math.random());
    setSuggestedPrompts(shuffled.slice(0, 4));
  }, []);

  useEffect(() => {
    let active = true;
    const fetchSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!active) return;
      if (session?.user) {
        setUid((prev) => prev || session.user.id);
        setAuthToken(session.access_token || null);
      }
    };
    fetchSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      if (session?.user) {
        setUid(session.user.id);
        setAuthToken(session.access_token || null);
      } else {
        setAuthToken(null);
      }
    });
    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  const historyKey = (id) => `coachChatHistory-${id}`;

  useEffect(() => {
    if (!uid) return;
    try {
      const stored = localStorage.getItem(historyKey(uid));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setChatLog(parsed);
        }
      }
    } catch {
      // ignore
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    try {
      localStorage.setItem(historyKey(uid), JSON.stringify(chatLog));
    } catch {
      // ignore persistence issues
    }
  }, [chatLog, uid]);

  const detectTopic = (q) => {
    const s = q.toLowerCase();
    const map = [
      { key: 'abdominals', words: ['abs', 'core', 'six pack', 'stomach'] },
      { key: 'chest', words: ['chest', 'pecs'] },
      { key: 'back', words: ['back', 'lats', 'lat'] },
      { key: 'biceps', words: ['biceps', 'bicep'] },
      { key: 'triceps', words: ['triceps', 'tricep'] },
      { key: 'glutes', words: ['glutes', 'glute', 'butt'] },
      { key: 'quadriceps', words: ['quads', 'quadriceps', 'legs', 'thighs'] },
      { key: 'hamstrings', words: ['hamstrings', 'hamstring'] },
      { key: 'shoulders', words: ['shoulders', 'delts', 'deltoids'] },
      { key: 'calves', words: ['calves', 'calf'] },
    ];
    for (const m of map) {
      if (m.words.some(w => s.includes(w))) return m.key;
    }
    return null;
  };

  const pickExercises = (primaryKey, count = 3) => {
    if (!primaryKey) return [];
    const pool = exercisesData.filter(ex => (ex.primaryMuscles || []).some(p => p.toLowerCase() === primaryKey));
    if (pool.length === 0) return [];
    const shuffled = [...pool].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  const sanitizeResponse = (text) => {
    if (text == null) return '';
    const str = typeof text === 'string' ? text : (() => {
      try {
        return JSON.stringify(text);
      } catch {
        return String(text);
      }
    })();
    if (!str) return '';
    const hadDash = dashCheckPattern.test(str);
    const sanitizedLines = str.split('\n').map((line) => {
      const leadingWhitespaceMatch = line.match(/^\s*/);
      const leadingWhitespace = leadingWhitespaceMatch ? leadingWhitespaceMatch[0] : '';
      const trimmed = line.trimStart();
      if (bulletPattern.test(trimmed)) {
        const withoutDash = trimmed.replace(bulletPattern, '');
        const cleaned = withoutDash.replace(dashReplacePattern, ' ');
        return `${leadingWhitespace}• ${cleaned}`.trimEnd();
      }
      const normalized = line.replace(rangePattern, (_, a, b) => `${a} to ${b}`);
      return normalized.replace(dashReplacePattern, ' ');
    });
    const sanitized = sanitizedLines.join('\n');
    if (hadDash) {
    }
    return sanitized;
  };

  const detectPlanIntent = (question = '') => {
    const s = question.toLowerCase();
    const containsAny = (list) => list.some((word) => s.includes(word));
    const hasWorkoutDomain = containsAny(PLAN_DOMAIN_WORKOUT);
    const hasMealDomain = containsAny(PLAN_DOMAIN_MEAL);
    const hasPlanContext = containsAny(PLAN_CONTEXT_WORDS);
    const hasTimeContext = containsAny(PLAN_TIME_WORDS);
    const hasPossessive = /\b(my|mine|our|your)\b/.test(s);
    const workoutIntent =
      hasWorkoutDomain &&
      (hasPlanContext || /\bmy workout\b/.test(s) || /\btoday's workout\b/.test(s) || (hasPossessive && hasTimeContext));
    const mealIntent =
      hasMealDomain &&
      (hasPlanContext || /\bmeal plan\b/.test(s) || /\bmeal(s)?\s+(today|tomorrow)\b/.test(s) || (hasPossessive && hasTimeContext));
    return { workout: workoutIntent, meal: mealIntent };
  };

  const resolvePlanStatus = async () => {
    if (!uid) return { workout: null, meal: null };
    const now = Date.now();
    const cached = planStatusRef.current;
    if (cached.lastChecked && (now - cached.lastChecked) < PLAN_STATUS_CACHE_MS) {
      return { workout: cached.workout, meal: cached.meal };
    }

    let workout = null;
    let meal = null;
    let programIds = [];

    try {
      const { data: workoutPlan, error: wpErr } = await supabase
        .from('workout_plans')
        .select('id')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!wpErr) {
        workout = !!workoutPlan;
      }
    } catch {
      // ignore lookup failures
    }

    try {
      const { data: programs, error: progErr } = await supabase
        .from('client_programs')
        .select('id')
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(5);
      if (!progErr) {
        programIds = (programs || []).map((p) => p.id).filter(Boolean);
        if (!programIds.length) {
          if (workout == null) workout = false;
          if (meal == null) meal = false;
        }
      }
    } catch {
      // ignore lookup failures
    }

    if (programIds.length) {
      if (workout !== true) {
        try {
          const { data: workoutDays, error: wdErr } = await supabase
            .from('workout_days')
            .select('id')
            .in('program_id', programIds)
            .limit(1);
          if (!wdErr) {
            workout = !!(workoutDays && workoutDays.length);
          }
        } catch {
          // ignore lookup failures
        }
      }

      try {
        const { data: mealDays, error: mdErr } = await supabase
          .from('meal_plan_days')
          .select('id')
          .in('program_id', programIds)
          .limit(1);
        if (!mdErr) {
          meal = !!(mealDays && mealDays.length);
        }
      } catch {
        // ignore lookup failures
      }
    }

    planStatusRef.current = { workout, meal, lastChecked: now };
    return { workout, meal };
  };

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTo({
        top: messagesRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [chatLog]);

  const handleQuestionSubmit = async () => {
    const question = userQuestion.trim();
    if (!question) return;

    setUserQuestion("");
    requestAnimationFrame(() => {
      if (taRef.current) {
        taRef.current.style.height = '48px';
        taRef.current.style.overflowY = 'hidden';
      }
    });

    const ts = new Date();
    const newEntry = { question, response: '', ts, isLoading: true };
    setChatLog(prev => [...prev, newEntry]);

    const planIntent = detectPlanIntent(question);
    if (planIntent.workout || planIntent.meal) {
      const status = await resolvePlanStatus();
      const needsWorkout = planIntent.workout && status.workout === false;
      const needsMeal = planIntent.meal && status.meal === false;
      if (needsWorkout || needsMeal) {
        const label = needsWorkout && needsMeal
          ? 'workout and meal plans'
          : (needsWorkout ? 'workout plan' : 'meal plan');
        const responseText = sanitizeResponse(
          `Cole is working right now to create your personalized ${label} for you. It will be ready in less than 24 hours.`
        );
        setChatLog(prev => {
          const updated = [...prev];
          const i = updated.length - 1;
          updated[i] = {
            ...updated[i],
            response: responseText,
            resources: [],
            topicLabel: null,
            ts,
            isLoading: false
          };
          return updated;
        });
        return;
      }
    }

    const topic = detectTopic(question);
    const suggested = pickExercises(topic, 3);

    try {
      const endpoint = '/coach-chat';
      const historyPayload = chatLog
        .slice(-6)
        .flatMap((entry) => {
          const turns = [];
          if (entry.question) {
            turns.push({ role: 'user', content: entry.question });
          }
          if (entry.response) {
            turns.push({ role: 'assistant', content: entry.response });
          }
          return turns;
        });
      const response = await apiFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: uid, message: question, accessToken: authToken, history: historyPayload })
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok) {
        const raw = await response.text();
        throw new Error(`HTTP ${response.status} from ${endpoint}: ${raw.slice(0, 200)}`);
      }

      let data;
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const raw = await response.text();
        throw new Error(`Non-JSON response from ${endpoint}: ${raw.slice(0, 200)}`);
      }

      setChatLog(prev => {
        const updated = [...prev];
        const i = updated.length - 1;
        updated[i] = {
          ...updated[i],
          response: sanitizeResponse(data?.error ? ("Error: " + data.error) : (data?.response || "")),
          resources: suggested,
          topicLabel: topic,
          ts,
          isLoading: false
        };
        return updated;
      });
    } catch (error) {
      setChatLog(prev => {
        const updated = [...prev];
        const i = updated.length - 1;
        updated[i] = {
          ...updated[i],
          response: sanitizeResponse("An error occurred while processing your question."),
          resources: suggested,
          topicLabel: topic,
          isLoading: false
        };
        return updated;
      });
    }

  };

  const handleInputChange = (e) => {
    setUserQuestion(e.target.value);
    const ta = taRef.current;
    if (ta) {
      const max = 140;
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, max) + 'px';
      ta.style.overflowY = ta.scrollHeight > max ? 'auto' : 'hidden';
    }
  };

  const goBack = () => { if (window.history.length > 1) window.history.back(); else window.location.href = "/"; };
  const openReport = () => { setShowReport(true); setReportStatus('idle'); };
  const closeReport = () => { setShowReport(false); setReportText(''); setReportStatus('idle'); };
  const openDisclaimerModal = () => setShowDisclaimer(true);
  const closeDisclaimerModal = () => setShowDisclaimer(false);
  const submitReport = async () => {
    if (!reportText.trim()) return;
    try {
      setReportStatus('sending');
      await new Promise(r => setTimeout(r, 600));
      setReportStatus('sent');
      setTimeout(() => closeReport(), 800);
    } catch (e) {
      setReportStatus('idle');
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split(/\n/);
    const elements = [];
    let list = [];
    const pushParagraph = (content, key) => elements.push(<p key={key}>{content}</p>);
    const pushList = (k) => { if (list.length) { elements.push(<ul key={k}>{list.map((li, i) => (<li key={i}>{li}</li>))}</ul>); list = []; } };

    const renderInline = (s, key) => {
      const parts = [];
      const linkRegex = /\[(.*?)\]\((https?:[^\s)]+)\)/g;
      const boldRegex = /\*\*(.*?)\*\*/g;
      let last = 0, m;
      while ((m = linkRegex.exec(s)) !== null) {
        if (m.index > last) parts.push(s.slice(last, m.index));
        parts.push(<a key={`${key}-a-${m.index}`} href={m[2]} target="_blank" rel="noreferrer">{m[1]}</a>);
        last = m.index + m[0].length;
      }
      if (last < s.length) parts.push(s.slice(last));
      return parts.map((p, i) => {
        if (typeof p !== 'string') return p;
        const segs = [];
        let l = 0, b;
        while ((b = boldRegex.exec(p)) !== null) {
          if (b.index > l) segs.push(p.slice(l, b.index));
          segs.push(<strong key={`${key}-b-${i}-${b.index}`}>{b[1]}</strong>);
          l = b.index + b[0].length;
        }
        if (l < p.length) segs.push(p.slice(l));
        return <span key={`${key}-s-${i}`}>{segs}</span>;
      });
    };

    lines.forEach((line, i) => {
      const t = line.trim();
      if (t.startsWith('- ') || t.startsWith('• ')) {
        const itemText = t.slice(2);
        list.push(renderInline(itemText, `li-${i}`));
      } else if (t.length === 0) {
        pushList(`ul-${i}`);
      } else {
        pushList(`ul-${i}`);
        pushParagraph(renderInline(line, `p-${i}`), `p-${i}`);
      }
    });
    pushList('ul-end');
    return elements;
  };

  const formatTime = (d) => {
    try { return new Date(d).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
    catch { return '' }
  };

  return (
    <>
      <div className={`cg-wrap ${isMobile ? 'mobile' : ''} ${animateIn ? 'enter' : ''}`}>
        <div className="cg-header">
          <button className="cg-back" onClick={goBack} aria-label="Back">
            <BackIcon className="icon" />
          </button>
          <h3 className="cg-title">AI Coach</h3>
          <button className="cg-more" onClick={openReport} aria-label="More">
            <EllipsisIcon className="icon" />
          </button>
        </div>

        <div className="cg-messages" ref={messagesRef}>
          {chatLog.length === 0 && (
            <div className="cg-empty">
              <p className="cg-placeholder">Ask about training, nutrition, sleep, or recovery.</p>
              <p className="cg-placeholder-subtitle">Need inspiration? Try one of these:</p>
              <div className="suggestions">
                {suggestedPrompts.map((p, i) => (
                  <div key={i} className="suggestion-bubble" onClick={() => setUserQuestion(p)}>{p}</div>
                ))}
              </div>
              <p className="cg-disclaimer-inline">
                {disclaimerInline}{' '}
                <button type="button" className="cg-disclaimer-link" onClick={openDisclaimerModal}>
                  Disclaimer
                </button>
              </p>
            </div>
          )}

          {chatLog.map((log, index) => (
            <div key={index} className="cg-exchange">
              <div className="cg-msg user">
                <div className="cg-bubble">{log.question}</div>
              </div>
              <div className="cg-timestamp right">{formatTime(log.ts)}</div>
              <div className="cg-msg assistant">
                <div className="cg-bubble">
                  {log.isLoading ? <TypingIndicator /> : renderMarkdown(log.response)}
                </div>
              </div>
              <div className="cg-timestamp">{formatTime(log.ts)}</div>

              {log.resources?.length > 0 && (
                <div className="cg-msg assistant">
                  <div className="cg-bubble">
                    <strong>Here are some helpful exercises{log.topicLabel ? ` for ${log.topicLabel}.` : '.'}</strong>
                    <div className="cg-resources">
                      {log.resources.map((ex, i) => (
                        <button key={i} className="ex-card" onClick={() => setOpenExercise(ex)}>
                          <div
                            className="ex-thumb"
                            data-label={ex.name}
                            style={{ backgroundImage: `url(${getImage((ex.images || [])[0] || '')})` }}
                          ></div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="cg-inputbar">
          <textarea
            ref={taRef}
            rows={1}
            value={userQuestion}
            onChange={handleInputChange}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuestionSubmit(); } }}
            placeholder="Ask away..."
          />
          <button className="send-btn" onClick={handleQuestionSubmit} aria-label="Send"><SendIcon className="send-icon" /></button>
        </div>
      </div>

      {showReport && (
        <div className="cg-modal-overlay" onClick={closeReport}>
          <div className="cg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cg-modal-header">
              <h3>Report a Problem</h3>
              <button className="cg-modal-close" onClick={closeReport} aria-label="Close">
                <CancelIcon className="icon" />
              </button>
            </div>
            <div className="cg-modal-body">
              <p className="cg-modal-desc">Describe the issue you ran into. Include steps to reproduce if possible.</p>
              <textarea
                className="cg-report-textarea"
                rows={4}
                value={reportText}
                onChange={(e) => setReportText(e.target.value)}
                placeholder="What went wrong?"
              />
              <div className="cg-modal-disclaimer">
                <strong>Legal note:</strong> {disclaimerFull}
              </div>
            </div>
            <div className="cg-modal-actions">
              <button className="cg-btn secondary" onClick={closeReport} disabled={reportStatus === 'sending'}>Cancel</button>
              <button className="cg-btn primary" onClick={submitReport} disabled={!reportText.trim() || reportStatus === 'sending'}>
                {reportStatus === 'sending' ? 'Sending…' : reportStatus === 'sent' ? 'Sent!' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDisclaimer && (
        <div className="cg-modal-overlay" onClick={closeDisclaimerModal}>
          <div className="cg-modal disclaimer" onClick={(e) => e.stopPropagation()}>
            <div className="cg-modal-header">
              <h3>{appName} Legal Disclaimer</h3>
              <button className="cg-modal-close" onClick={closeDisclaimerModal} aria-label="Close disclaimer">
                <CancelIcon className="icon" />
              </button>
            </div>
            <div className="cg-modal-body">
              <div className="cg-disclaimer-body">
                <p>{disclaimerFull}</p>
                <p className="cg-disclaimer-subtext">{disclaimerInline}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <ExerciseModal isOpen={!!openExercise} onClose={() => setOpenExercise(null)} exercise={openExercise} getImage={getImage} />
    </>
  );
};

export default CoachChatbot;
